<script setup lang="ts">
import { useHubIndexing } from '@/data/Hubs/useHubIndexing'
import { useHubReindexing } from '@/data/Hubs/useHubReindexing'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { pluralize } from '@/shared/utils/string'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import DotIndicator from '@/uiKit/DotIndicator.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import { computed, ref } from 'vue'

const props = defineProps<{ hubId: string }>()

const { workspaceId } = useWorkspaceContext()
const { isStale, isProcessing, isIndexing, issueCount, isStateLoaded } = useHubIndexing({
  workspaceId: workspaceId,
  hubId: () => props.hubId,
})

const { mutate: reindex } = useHubReindexing({ workspaceId })

const isConfirmationDialogOpen = ref(false)

const isIndexable = computed(() => {
  return isStale.value && !isProcessing.value
})

const clampedIssueCountLabel = computed(() => {
  const clampedCount = Math.min(issueCount.value, 10)
  const label = issueCount.value > clampedCount ? `${clampedCount}+` : `${clampedCount}`
  return label + ' ' + pluralize(clampedCount, 'file', 'files')
})

function onClick() {
  if (issueCount.value > 0) {
    isConfirmationDialogOpen.value = true
  } else {
    reindex({ hubId: props.hubId })
  }
}

function onConfirm() {
  isConfirmationDialogOpen.value = false
  reindex({ hubId: props.hubId })
}
</script>

<template>
  <DarwinButton
    v-if="isStateLoaded"
    size="sm"
    rounded
    :inert="!isIndexable"
    variant="neutral"
    :class="[isIndexable ? 'cursor-pointer' : 'bg-background-transparent']"
    @click.stop.prevent="onClick"
  >
    <template #leading-icon>
      <DotIndicator
        v-if="isProcessing || isIndexing"
        :reason="{ cause: 'computing' }"
      />
      <IconSprite
        v-else-if="isStale"
        size="sm"
        icon="switch"
        class="text-text-error"
      />
      <DotIndicator
        v-else-if="issueCount > 0"
        :reason="{
          cause: 'warning',
          detail: `${issueCount} ${pluralize(issueCount, 'file', 'files')} with issues`,
        }"
      />
      <IconSprite
        v-else
        size="sm"
        icon="check-circle-fill"
        class="text-text-success"
      />
    </template>
    <template #default>
      <template v-if="isIndexing">Indexing…</template>
      <template v-else-if="isProcessing">Processing…</template>
      <template v-else-if="isStale">Re-index</template>
      <template v-else-if="issueCount > 0">
        Ready ({{ clampedIssueCountLabel }} with issues)
      </template>
      <template v-else>Ready</template>
    </template>
  </DarwinButton>
  <ConfirmationDialog
    title="Re-index with missing files?"
    :description="`Index will not include ${issueCount} ${pluralize(issueCount, 'file', 'files')} with issues.`"
    :open="isConfirmationDialogOpen"
    confirm-text="Re-index"
    variant="black"
    @confirm="onConfirm"
    @close="isConfirmationDialogOpen = false"
    @cancel="isConfirmationDialogOpen = false"
  />
</template>
